import { captureApiError } from "./observability/sentry";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";
const TOKEN_KEY = "auth_token";



export const tokenManager = {
  get: (): string | null => localStorage.getItem(TOKEN_KEY),
  set: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
  clear: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem("evooluir-logged-in");
    localStorage.removeItem("evooluir-demo-role");
    localStorage.removeItem("evooluir-user-email");
  },
};

/**
 * Error for a completed HTTP response with a non-OK status. Carries `status`
 * so error handling and Sentry capture branch on the status code instead of
 * matching message strings, and so genuine network/parse failures (which throw
 * plain Errors) stay distinguishable from HTTP errors.
 */
export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isFormData: boolean = false,
    skipAuth: boolean = false
  ): Promise<T> {
    const token = tokenManager.get();

    const headers: Record<string, string> = {};

    // Copy existing headers if they exist
    if (options.headers) {
      if (options.headers instanceof Headers) {
        options.headers.forEach((value, key) => {
          headers[key] = value;
        });
      } else if (Array.isArray(options.headers)) {
        options.headers.forEach(([key, value]) => {
          headers[key] = value;
        });
      } else {
        Object.assign(headers, options.headers);
      }
    }

    // Only set Content-Type for JSON, FormData sets it automatically with boundary
    if (!isFormData) {
      headers["Content-Type"] = "application/json";
    }

    if (!skipAuth) {
      const token = tokenManager.get();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...options,
        headers: headers as HeadersInit,
        credentials: 'include',
        mode: 'cors',
      });

      if (response.status === 401) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || "Authentication required";

        const isTokenError =
          errorMessage.toLowerCase().includes("token") ||
          errorMessage.toLowerCase().includes("expired") ||
          (errorMessage.toLowerCase().includes("unauthorized") &&
            !errorMessage.toLowerCase().includes("permission"));

        if (isTokenError && token) {
          tokenManager.clear();
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }

        throw new ApiError(errorMessage, 401);
      }

      if (response.status === 403) {
        throw new ApiError(
          "You do not have permission to perform this action",
          403,
        );
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new ApiError(
          errorData.message || `Request failed with status ${response.status}`,
          response.status,
        );
        captureApiError(err, {
          url: endpoint,
          method: options.method ?? "GET",
          status: response.status,
        });
        throw err;
      }

      // Handle responses - read text first to check if empty
      const text = await response.text();

      // If no content or empty string, return undefined (for DELETE requests that return empty body)
      if (!text || text.trim() === "") {
        return undefined as T;
      }

      // Try to parse as JSON
      try {
        return JSON.parse(text) as T;
      } catch {
        // If not JSON, return as text
        return text as unknown as T;
      }
    } catch (error) {
      // HTTP errors throw ApiError and were already classified above (5xx
      // captured by status; 401/403/other-4xx skipped). Only genuine
      // network/parse failures reach capture here, so a 401 body message like
      // "Unauthorized" no longer leaks through as network_error.
      if (error instanceof ApiError) {
        throw error;
      }
      const errObj = error instanceof Error ? error : new Error(String(error));
      captureApiError(errObj, {
        url: endpoint,
        method: options.method ?? "GET",
      });
      throw error instanceof Error
        ? error
        : new Error("An unexpected error occurred");
    }
  }

  get<T>(endpoint: string, skipAuth: boolean = false): Promise<T> {
    return this.request<T>(endpoint, { method: "GET" }, false, skipAuth);
  }

  post<T>(
    endpoint: string,
    data?: unknown,
    skipAuth: boolean = false
  ): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(
      endpoint,
      {
        method: "POST",
        body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      },
      isFormData,
      skipAuth
    );
  }

  put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  patch<T>(endpoint: string, data?: unknown): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(
      endpoint,
      {
        method: "PATCH",
        body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      },
      isFormData
    );
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: "DELETE" });
  }
}

export const api = new ApiClient(API_BASE_URL);
export { API_BASE_URL };
